import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
  Popconfirm,
} from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import { useEffect, useState } from 'react'
import { api } from 'services/api'

const { Title } = Typography
const { Option } = Select

const EventsPage = () => {
  const [events, setEvents] = useState([])
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(false)
  const [clientsLoading, setClientsLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingEvent, setEditingEvent] = useState(null)
  const [form] = Form.useForm()

  const userRole = localStorage.getItem('USER_ROLE')
  const isAdminNlb = userRole === 'ADMIN_NLB'

  const loadEvents = async () => {
    setLoading(true)

    try {
      const response = await api.get('/events')
      const data = response?.data || response
      setEvents(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('ERRO EVENTS:', error)
      message.error('Erro ao carregar eventos')
      setEvents([])
    }

    setLoading(false)
  }

  const loadClients = async () => {
    if (!isAdminNlb) return

    setClientsLoading(true)

    try {
      const response = await api.get('/clients')
      const data = response?.data || response
      setClients(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('ERRO CLIENTS:', error)
      message.error('Erro ao carregar clientes')
      setClients([])
    }

    setClientsLoading(false)
  }

  const openCreateModal = () => {
    setEditingEvent(null)
    form.resetFields()
    form.setFieldsValue({
      status: 'ACTIVE',
    })
    setModalOpen(true)

    if (isAdminNlb) {
      loadClients()
    }
  }

  const openEditModal = (record) => {
    setEditingEvent(record)

    form.setFieldsValue({
      name: record.name,
      slug: record.slug,
      clientId: record.clientId || record.client?.id || undefined,
      status: record.status || 'ACTIVE',
    })

    setModalOpen(true)

    if (isAdminNlb) {
      loadClients()
    }
  }

  const handleSubmit = async (values) => {
    try {
      const payload = {
        name: values.name,
        slug: values.slug,
        status: values.status || 'ACTIVE',
      }

      if (isAdminNlb && values.clientId) {
        payload.clientId = values.clientId
      }

      if (editingEvent) {
        await api.patch(`/events/${editingEvent.id}`, payload)
        message.success('Evento atualizado')
      } else {
        await api.post('/events', payload)
        message.success('Evento criado')
      }

      setModalOpen(false)
      setEditingEvent(null)
      form.resetFields()
      loadEvents()
    } catch (error) {
      console.error('ERRO SAVE EVENT:', error?.response?.data || error)
      message.error(error?.response?.data?.message || 'Erro ao salvar evento')
    }
  }

  const handleDelete = async (record) => {
    try {
      await api.delete(`/events/${record.id}`)
      message.success('Evento excluído')
      loadEvents()
    } catch (error) {
      console.error('ERRO DELETE EVENT:', error?.response?.data || error)
      message.error(error?.response?.data?.message || 'Erro ao excluir evento')
    }
  }

  useEffect(() => {
    loadEvents()
  }, [])

  return (
    <>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <Title level={2} style={{ margin: 0 }}>Eventos</Title>
          <p>Cadastre e gerencie os eventos da roleta.</p>
        </div>

        <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
          Novo Evento
        </Button>
      </Space>

      <Card>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={events}
          columns={[
            {
              title: 'Evento',
              dataIndex: 'name',
            },
            {
              title: 'Slug',
              dataIndex: 'slug',
            },
            {
              title: 'Cliente',
              render: (_, record) =>
                record.client?.name ||
                record.client?.tradeName ||
                record.client?.companyName ||
                record.client?.razaoSocial ||
                '-',
            },
            {
              title: 'Status',
              dataIndex: 'status',
              render: (status) => (
                <Tag color={status === 'ACTIVE' ? 'green' : 'default'}>
                  {status}
                </Tag>
              ),
            },
            {
              title: 'Link do Game',
              render: (_, record) => {
                const game = record.games?.[0]

                if (!game) {
                  return <Tag color="red">Sem game</Tag>
                }

                const url = `${window.location.origin}/play/${game.slug}`

                return (
                  <Space>
                    <Button type="primary" onClick={() => window.open(url, '_blank')}>
                      Abrir Game
                    </Button>

                    <Button
                      onClick={() => {
                        navigator.clipboard.writeText(url)
                        message.success('Link copiado!')
                      }}
                    >
                      Copiar Link
                    </Button>
                  </Space>
                )
              },
            },
            {
              title: 'Ações',
              render: (_, record) => (
                <Space>
                  <Button icon={<EditOutlined />} onClick={() => openEditModal(record)}>
                    Editar
                  </Button>

                  {isAdminNlb && (
                    <Popconfirm
                      title="Deseja excluir este evento?"
                      okText="Sim"
                      cancelText="Não"
                      onConfirm={() => handleDelete(record)}
                    >
                      <Button danger icon={<DeleteOutlined />}>
                        Excluir
                      </Button>
                    </Popconfirm>
                  )}
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title={editingEvent ? 'Editar Evento' : 'Novo Evento'}
        visible={modalOpen}
        onCancel={() => {
          setModalOpen(false)
          setEditingEvent(null)
          form.resetFields()
        }}
        onOk={() => form.submit()}
        okText="Salvar"
        cancelText="Cancelar"
      >
        <Form layout="vertical" form={form} onFinish={handleSubmit}>
          <Form.Item name="name" label="Nome do evento" rules={[{ required: true }]}>
            <Input placeholder="Ex: Naturaltech 2026" />
          </Form.Item>

          <Form.Item name="slug" label="Slug da roleta" rules={[{ required: true }]}>
            <Input placeholder="ex: naturaltech-2026" />
          </Form.Item>

          {isAdminNlb && (
            <Form.Item name="clientId" label="Cliente cadastrado" rules={[{ required: true }]}>
              <Select
                showSearch
                allowClear
                loading={clientsLoading}
                placeholder="Busque pelo cliente"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  String(option?.children || '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {clients.map((client) => {
                  const label =
                    client.name ||
                    client.tradeName ||
                    client.companyName ||
                    client.razaoSocial ||
                    `Cliente #${client.id}`

                  return (
                    <Option key={client.id} value={client.id}>
                      {label}
                    </Option>
                  )
                })}
              </Select>
            </Form.Item>
          )}

          <Form.Item name="status" label="Status" initialValue="ACTIVE">
            <Select>
              <Option value="ACTIVE">ACTIVE</Option>
              <Option value="DRAFT">DRAFT</Option>
              <Option value="FINISHED">FINISHED</Option>
              <Option value="CANCELLED">CANCELLED</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default EventsPage